/*
 * EU withdrawal page — two-step flow.
 * Step 1: statement form (client validation mirrors the worker's rules).
 * Step 2: review screen; the final button ("Confirm withdrawal") POSTs the
 * payload to the serverless endpoint, which sends the acknowledgement email.
 * No secrets here: the endpoint is a public URL, abuse protection lives
 * server-side (honeypot, HMAC-signed form token, validation, rate limit).
 */
(() => {
  const init = () => {
    const root = document.querySelector('[data-hd-withdrawal]');
    if (!root) return;

    const configEl = root.querySelector('[data-hd-withdrawal-config]');
    if (!configEl) return; // fallback (email-only) mode — nothing to wire up
    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      return;
    }
    const strings = config.strings || {};
    const endpoint = String(config.endpoint || '').replace(/\/+$/, '');
    if (!endpoint) return;

    const form = root.querySelector('[data-hd-w-step="form"]');
    const review = root.querySelector('[data-hd-w-step="review"]');
    const success = root.querySelector('[data-hd-w-step="success"]');
    const errorBox = root.querySelector('[data-hd-w-error]');
    const confirmButton = root.querySelector('[data-hd-w-confirm]');
    const backButton = root.querySelector('[data-hd-w-back]');
    if (!form || !review || !success || !confirmButton) return;

    let formToken = '';
    let tokenIssuedAt = 0;

    const fetchToken = async () => {
      try {
        const response = await fetch(`${endpoint}/api/withdrawal/token`, { method: 'GET' });
        if (!response.ok) return;
        const data = await response.json();
        if (data && typeof data.token === 'string') {
          formToken = data.token;
          tokenIssuedAt = Date.now();
        }
      } catch (error) {
        /* token refetch happens on confirm if needed */
      }
    };
    fetchToken();

    const showStep = (name) => {
      form.hidden = name !== 'form';
      review.hidden = name !== 'review';
      success.hidden = name !== 'success';
      root.querySelectorAll('[data-hd-w-step-pill]').forEach((pill) => {
        const pillName = pill.getAttribute('data-hd-w-step-pill');
        pill.classList.toggle('is-active', pillName === name || (name === 'success' && pillName === 'review'));
        pill.classList.toggle('is-done', name === 'success' || (name === 'review' && pillName === 'form'));
      });
      const heading = name === 'review'
        ? root.querySelector('[data-hd-w-review-heading]')
        : name === 'success'
          ? root.querySelector('[data-hd-w-success-heading]')
          : null;
      if (heading) heading.focus({ preventScroll: false });
    };

    const hideError = () => {
      errorBox.hidden = true;
    };

    const showError = () => {
      errorBox.querySelector('[data-hd-w-error-title]').textContent = strings.errorTitle || 'Something went wrong';
      errorBox.querySelector('[data-hd-w-error-body]').textContent = strings.errorBody || 'Please try again.';
      errorBox.hidden = false;
      errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const setFieldError = (name, message) => {
      const el = root.querySelector(`[data-hd-w-field-error="${name}"]`);
      const input = form.elements[name];
      if (el) {
        el.textContent = message || '';
        el.hidden = !message;
      }
      if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
    };

    const values = () => ({
      name: form.elements.name.value.trim(),
      email: form.elements.email.value.trim(),
      orderDetails: form.elements.orderDetails.value.trim(),
      reason: form.elements.reason.value.trim(),
      website: form.elements.website.value, // honeypot, expected empty
    });

    const validate = (data) => {
      let ok = true;
      ['name', 'email', 'orderDetails'].forEach((field) => {
        if (!data[field]) {
          setFieldError(field, strings.validationRequired || 'Please fill in this field.');
          ok = false;
        } else {
          setFieldError(field, '');
        }
      });
      if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(data.email)) {
        setFieldError('email', strings.validationEmail || 'Please enter a valid email address.');
        ok = false;
      }
      return ok;
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      hideError();
      const data = values();
      if (!validate(data)) {
        const firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }
      review.querySelectorAll('[data-hd-w-review]').forEach((slot) => {
        const key = slot.getAttribute('data-hd-w-review');
        slot.textContent = data[key] || strings.reviewNone || '—';
      });
      showStep('review');
    });

    if (backButton) {
      backButton.addEventListener('click', () => {
        hideError();
        showStep('form');
        form.elements.name.focus();
      });
    }

    confirmButton.addEventListener('click', async () => {
      hideError();
      const data = values();
      if (!validate(data)) {
        showStep('form');
        return;
      }
      // Tokens expire server-side; refresh if older than 30 minutes or missing.
      if (!formToken || Date.now() - tokenIssuedAt > 30 * 60 * 1000) {
        await fetchToken();
      }

      confirmButton.disabled = true;
      const confirmLabel = confirmButton.textContent;
      confirmButton.textContent = strings.submitting || confirmLabel;

      const postSubmission = () => fetch(`${endpoint}/api/withdrawal/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          orderDetails: data.orderDetails,
          reason: data.reason,
          website: data.website,
          token: formToken,
          locale: config.locale || 'en',
          country: config.country || '',
          page: window.location.pathname,
        }),
      });

      try {
        let response = await postSubmission();
        let result = await response.json().catch(() => null);
        if (response.status === 400 && result && result.error === 'invalid_token') {
          await fetchToken();
          response = await postSubmission();
          result = await response.json().catch(() => null);
        }
        if (!response.ok || !result || result.ok !== true) throw new Error('submit_failed');

        success.querySelector('[data-hd-w-success="reference"]').textContent = result.id || '—';
        const submittedAt = result.submittedAt ? new Date(result.submittedAt) : new Date();
        const timeSlot = success.querySelector('[data-hd-w-success="time"]');
        timeSlot.textContent = `${submittedAt.toLocaleString(config.locale || undefined, {
          dateStyle: 'long',
          timeStyle: 'short',
        })} (${submittedAt.toISOString()} UTC)`;
        success.querySelector('[data-hd-w-success="email"]').textContent = data.email;
        showStep('success');
      } catch (error) {
        showError();
      } finally {
        confirmButton.disabled = false;
        confirmButton.textContent = confirmLabel;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
